/**
 * Typed wrappers for @general-backoffice/core components.
 * The library's runtime code supports wider types (ReactNode for title/lead)
 * but the type declarations specify narrower types (string).
 * These wrappers provide the correct types without type assertions.
 */
import { Page, StatsCard, Table } from "@general-backoffice/core";
import type { MouseEventHandler, ReactNode } from "react";

/**
 * Create a typed wrapper component from a library component.
 * Uses ComponentType<never> to avoid any/unknown spreading issues.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function typed<P>(Component: React.ComponentType<any>): React.FC<P> {
  const Wrapped: React.FC<P> = (props) => <Component {...props} />;
  return Wrapped;
}

// ─── Login Page ────────────────────────────────────────────
export interface TypedLoginPageProps {
  title?: ReactNode;
  lead?: ReactNode;
  emailPlaceholder?: string;
  passwordPlaceholder?: string;
  rememberLabel?: string;
  submitText?: string;
  forgottenPasswordText?: string;
  registerText?: string;
  invalidEmailFeedbackText?: string;
  invalidPasswordFeedbackText?: string;
  onSubmit?: (state: { email: string; password: string }) => Promise<unknown>;
  onClickForgottenPassword?: MouseEventHandler<HTMLButtonElement>;
  onClickRegister?: () => void;
  showRegisterAsButton?: boolean;
}

export const TypedLoginPage = typed<TypedLoginPageProps>(Page.Login);

// ─── Auth Form Container Page ──────────────────────────────
export interface TypedAuthFormPageProps {
  title?: ReactNode;
  lead?: ReactNode;
  onSubmit?: () => Promise<unknown>;
  children?: ReactNode;
  submitText?: string;
  leftLink?: Record<string, unknown>;
  rightLink?: Record<string, unknown>;
}

export const TypedAuthFormPage = typed<TypedAuthFormPageProps>(Page.AuthFormContainerPage);

// ─── Stats Number Card ─────────────────────────────────────
export interface TypedNumberCardProps {
  color?: string;
  title: string;
  suffix?: string;
  number: number | string;
  description?: ReactNode;
  icon?: string;
  isDark?: boolean;
}

export const TypedNumberCard = typed<TypedNumberCardProps>(StatsCard.Number);

// ─── Table ──────────────────────────────────────────────────
// The library's Table types use strict function parameter checking
// that prevents passing columns with specific row types.
// This wrapper uses `any` for row/column types to match runtime behavior.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface TypedTableColumn<T = any> {
  property?: string;
  formattedContent?: (row: T, index: number) => ReactNode;
  isTh?: boolean;
  compressed?: boolean;
  hidden?: boolean;
  name?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sort?: (newStatus: any) => void;
  sortStatus?: string | null;
}

export interface TypedTableProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: TypedTableColumn<any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDataChange?: (rows: any[], trigger: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowClick?: (row: any) => void;
  isDark?: boolean;
  isLoading?: boolean;
  rowHeight?: number;
  rowAmount?: number;
  noContentMessage?: ReactNode;
  selectable?: boolean;
  sortable?: boolean;
}

export const TypedTable = typed<TypedTableProps>(Table);
